// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Button, Spinner, Textarea, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import {
    AttachRegular,
    DismissCircle20Regular,
    MicRegular,
    MoreCircle24Regular,
    SendRegular,
} from '@fluentui/react-icons';
import debug from 'debug';
import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import React, { useRef } from 'react';
import { Constants } from '../../Constants';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessageType } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import {
    addAlert,
    removeAlert,
    updateAppErrorState,
    updateInputDisabledState,
} from '../../redux/features/app/appSlice';
import {
    editConversationArchiveSources,
    editConversationInput,
    updateBotResponseStatus,
} from '../../redux/features/conversations/conversationsSlice';
import { Breakpoints, customTokens } from '../../styles';
import { Alerts } from '../shared/Alerts';
import { SpeechService } from './../../libs/services/SpeechService';
import { updateUserIsTyping } from './../../redux/features/conversations/conversationsSlice';
import { ChatStatus } from './ChatStatus';
import { IArchiveSources } from '../../libs/models/ArchiveSources';
import { ChatScope } from '../../redux/features/conversations/ChatState';
import { IAskResult } from '../../libs/semantic-kernel/model/AskResult';

const log = debug(Constants.debug.root).extend('chat-input');

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(0, 0, 0, '42px'),
        ...Breakpoints.medium({
            marginLeft: '10px',
            marginRight: '10px',
        }),
    },
    typingIndicator: {
        maxHeight: '28px',
    },
    archivePillsContainer: {
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        width: '95%',
        flexWrap: 'wrap',
    },
    archivePillDesc: {
        marginRight: '10px',
        fontWeight: '600',
    },
    archivePills: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    archivePill: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        ...shorthands.margin('8px', '10px', '5px', '0'),
        opacity: 1,
        ...shorthands.padding('1px', '4px', '1px', 0),
        ...shorthands.borderRadius('20px'),
        width: 'auto',
        color: '#ffffff',
        textAlign: 'center',
    },
    archivePillValue: {
        ...shorthands.flex(1, 0, 'auto'),
        lineHeight: '18px',
        ...shorthands.padding('0px', '5px', '1px', '10px'),
        fontWeight: 600,
        ...Breakpoints.small({
            fontSize: '12px',
        }),
    },
    archivePillIcon: {
        height: '20px',
        cursor: 'pointer',

        '&:hover': {
            '> svg': {
                color: '#000000',
            },
        },
    },
    moreArchivePills: {
        marginLeft: '5px',
        ...shorthands.padding('4px', '0', '0px', '0'),
        color: 'black',
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '30px',

        '&:hover': {
            '> svg': {
                color: customTokens.ekfbTeal,
            },
        },
    },
    textareaHolder: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
    },
    input: {
        width: '100%',
        ...shorthands.borderRadius(customTokens.borderRadiusNone),
        borderBottomLeftRadius: customTokens.borderRadiusMedium,
        borderBottomRightRadius: customTokens.borderRadiusMedium,
        marginBottom: '10px',
    },
    contentHolder: {
        display: 'flex',
        flexDirection: 'row',
    },
    content: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: '95%',
    },
    textarea: {
        maxHeight: '80px',
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
    },
    essentials: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: 'auto', // align to right
    },
    functional: {
        display: 'flex',
        flexDirection: 'row',
    },
    dragAndDrop: {
        ...shorthands.border(tokens.strokeWidthThick, ' solid', tokens.colorBrandStroke1),
        ...shorthands.padding('8px'),
        textAlign: 'center',
        backgroundColor: tokens.colorNeutralBackgroundInvertedDisabled,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorBrandForeground1,
        caretColor: 'transparent',
    },
    sendButtonContainer: {
        display: 'flex',
        alignItems: 'end',
        marginBottom: '20px',
    },
    sendButton: {
        paddingTop: '0px',
    },
});

interface ChatInputProps {
    isDraggingOver?: boolean;
    onDragLeave: React.DragEventHandler<HTMLDivElement | HTMLTextAreaElement>;
    onSubmit: (options: GetResponseOptions) => Promise<IAskResult | null>;
}

export const ChatInput: React.FC<ChatInputProps> = ({ isDraggingOver, onDragLeave, onSubmit }) => {
    const classes = useClasses();
    const { instance, inProgress } = useMsal();
    const chat = useChat();
    const dispatch = useAppDispatch();
    const [value, setValue] = React.useState('');
    const [recognizer, setRecognizer] = React.useState<speechSdk.SpeechRecognizer>();
    const [isListening, setIsListening] = React.useState(false);
    const [documentImporting, setDocumentImporting] = React.useState(false);
    const documentFileRef = useRef<HTMLInputElement | null>(null);
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const [pillsToShow, setPillsToShow] = React.useState(5);
    const { activeUserInfo, alerts, appErrorState, isInputDisabled } = useAppSelector((state: RootState) => state.app);

    const chatState = conversations[selectedId];
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        // Focus on the text area when the selected conversation changes
        textAreaRef.current?.focus();
    }, [selectedId]);

    React.useEffect(() => {
        let timeout: NodeJS.Timeout | undefined;

        const limitAlertsCount = alerts.filter(
            (alert) => alert.message.startsWith('Message limit:') || alert.message.startsWith('Token limit:'),
        ).length;

        if (limitAlertsCount > 0) {
            timeout = setTimeout(() => {
                const messageLimitIndex = alerts.findIndex((alert) => alert.message.startsWith('Message limit:'));

                if (messageLimitIndex > -1) {
                    dispatch(removeAlert(messageLimitIndex));
                }

                const tokenLimitIndex = alerts.findIndex((alert) => alert.message.startsWith('Token limit:'));

                if (tokenLimitIndex > -1) {
                    dispatch(removeAlert(tokenLimitIndex));
                }

                dispatch(updateInputDisabledState(false));
                dispatch(updateAppErrorState(false));
            }, limitAlertsCount * 10000);
        }

        return () => {
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alerts]);

    React.useEffect(() => {
        async function initSpeechRecognizer() {
            const speechService = new SpeechService(process.env.REACT_APP_BACKEND_URI as string);
            const response = await speechService.getSpeechTokenAsync(
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
            if (response.isSuccess) {
                const recognizer = speechService.getSpeechRecognizerAsyncWithValidKey(response);
                setRecognizer(recognizer);
            }
        }

        initSpeechRecognizer().catch((e) => {
            const errorDetails = e instanceof Error ? e.message : String(e);
            const errorMessage = `Unable to initialize speech recognizer. Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    }, [dispatch, instance, inProgress]);

    React.useEffect(() => {
        const chatState = conversations[selectedId];
        setValue(chatState.input);
    }, [conversations, selectedId]);

    const handleSpeech = () => {
        setIsListening(true);
        if (recognizer) {
            recognizer.recognizeOnceAsync((result) => {
                if (result.reason === speechSdk.ResultReason.RecognizedSpeech) {
                    if (result.text && result.text.length > 0) {
                        handleSubmit(result.text);
                    }
                }
                setIsListening(false);
            });
        }
    };

    const handleImport = (dragAndDropFiles?: FileList) => {
        const files = dragAndDropFiles ?? documentFileRef.current?.files;

        if (files && files.length > 0) {
            setDocumentImporting(true);
            // Deep copy the FileList into an array so that the function
            // maintains a list of files to import before the import is complete.
            const filesArray = Array.from(files);
            void chat.importDocument(selectedId, filesArray).finally(() => {
                setDocumentImporting(false);
            });
        }

        // Reset the file input so that the onChange event will
        // be triggered even if the same file is selected again.
        if (documentFileRef.current?.value) {
            documentFileRef.current.value = '';
        }
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message) => {
        if (value.trim() === '') {
            return; // only submit if value is not empty
        }

        setValue('');

        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Thomas the AI Engine is typing' }));
        dispatch(updateInputDisabledState(true));

        onSubmit({ value, messageType, chatId: selectedId })
            .then((askResult: IAskResult | null) => {
                if (askResult && !appErrorState) {
                    dispatch(updateInputDisabledState(false));
                }
            })
            .catch((error) => {
                const message = `Error submitting chat input: ${(error as Error).message}`;

                log(message);

                dispatch(
                    addAlert({
                        type: AlertType.Error,
                        message,
                    }),
                );
            });
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
        onDragLeave(e);
        handleImport(e.dataTransfer.files);
    };

    const onRemoveSelection = (index: number) => {
        const updatedSelectedSources: IArchiveSources[] = [];

        chatState.selectedSources?.forEach((source, i) => {
            if (i !== index) {
                updatedSelectedSources.push(source);
            }
        });

        updateConversationArchiveSources(updatedSelectedSources, chatState.selectedSources?.length === 1).catch(
            (err) => {
                log('An error occured whilst editing chat state', err);
            },
        );
    };

    const updateConversationArchiveSources = async (
        selectedSources: IArchiveSources[],
        everythingSelected: boolean,
    ) => {
        const updatedSelectedSources = everythingSelected ? [] : selectedSources;

        await chat.editChat(
            selectedId,
            chatState.title,
            chatState.memoryBalance,
            chatState.chatScope,
            chatState.isHidden,
            updatedSelectedSources,
        );

        dispatch(
            editConversationArchiveSources({
                id: selectedId,
                newArchiveSources: updatedSelectedSources,
            }),
        );
    };

    return (
        <div className={classes.root}>
            <div className={classes.typingIndicator}>
                <ChatStatus />
            </div>
            {chatState.chatScope === ChatScope.Archive &&
                chatState.selectedSources &&
                chatState.selectedSources.length > 0 && (
                    <div className={classes.archivePillsContainer}>
                        <span className={classes.archivePillDesc}>{`Selected source${
                            chatState.selectedSources.length > 1 ? 's' : ''
                        }: `}</span>
                        <div className={classes.archivePills}>
                            {chatState.selectedSources.slice(0, pillsToShow).map((source, index) => {
                                const name = source.PlatformName;
                                const background =
                                    source.PlatformNameColor !== '' ? source.PlatformNameColor : customTokens.ekfbTeal;

                                return (
                                    <div
                                        key={index}
                                        className={classes.archivePill}
                                        style={{ backgroundColor: background }}
                                    >
                                        <div className={classes.archivePillValue}>{name}</div>
                                        <div
                                            className={classes.archivePillIcon}
                                            onClick={() => {
                                                onRemoveSelection(index);
                                            }}
                                        >
                                            <DismissCircle20Regular color={'#ffffff'} />
                                        </div>
                                    </div>
                                );
                            })}
                            {pillsToShow < chatState.selectedSources.length && (
                                <div
                                    title="Show more"
                                    className={classes.moreArchivePills}
                                    onClick={() => {
                                        setPillsToShow(pillsToShow + 5);
                                    }}
                                >
                                    <MoreCircle24Regular />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            <div className={classes.contentHolder}>
                <div className={classes.content}>
                    <Alerts />
                    <div className={classes.textareaHolder}>
                        <Textarea
                            ref={textAreaRef}
                            id="chat-input"
                            resize="vertical"
                            textarea={{
                                className: isDraggingOver
                                    ? mergeClasses(classes.dragAndDrop, classes.textarea)
                                    : classes.textarea,
                            }}
                            disabled={isInputDisabled}
                            placeholder="Ask me a question..."
                            className={classes.input}
                            value={isDraggingOver ? 'Drop your files here' : value}
                            onDrop={handleDrop}
                            onFocus={() => {
                                // update the locally stored value to the current value
                                const chatInput = document.getElementById('chat-input');
                                if (chatInput) {
                                    setValue((chatInput as HTMLTextAreaElement).value);
                                }
                                // User is considered typing if the input is in focus
                                dispatch(
                                    updateUserIsTyping({
                                        userId: activeUserInfo?.id,
                                        chatId: selectedId,
                                        isTyping: true,
                                    }),
                                );
                            }}
                            onChange={(_event, data) => {
                                if (isDraggingOver) {
                                    return;
                                }

                                setValue(data.value);
                                dispatch(editConversationInput({ id: selectedId, newInput: data.value }));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    handleSubmit(value);
                                }
                            }}
                            onBlur={() => {
                                // User is considered not typing if the input is not  in focus
                                dispatch(
                                    updateUserIsTyping({
                                        userId: activeUserInfo?.id,
                                        chatId: selectedId,
                                        isTyping: false,
                                    }),
                                );
                            }}
                        />
                    </div>
                </div>
                <div className={classes.sendButtonContainer}>
                    <Button
                        className={classes.sendButton}
                        appearance="transparent"
                        icon={<SendRegular />}
                        disabled={isInputDisabled}
                        onClick={() => {
                            handleSubmit(value);
                        }}
                    />
                </div>
            </div>
            <div className={classes.controls}>
                {chatState.chatScope !== ChatScope.Archive && (
                    <div className={classes.functional}>
                        {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
                        <input
                            type="file"
                            ref={documentFileRef}
                            style={{ display: 'none' }}
                            accept=".txt,.pdf,.md,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                            multiple={true}
                            onChange={() => {
                                handleImport();
                            }}
                        />
                        <Button
                            disabled={documentImporting || isInputDisabled}
                            appearance="primary"
                            icon={<AttachRegular />}
                            onClick={() => documentFileRef.current?.click()}
                            title="Supported file formats: .txt, .pdf, .md, .doc, .docx, .ppt, .pptx, .xls, .xlsx (Max size: 10MB)"
                        >
                            Upload a document
                        </Button>
                        {documentImporting && <Spinner size="tiny" />}
                    </div>
                )}
                <div className={classes.essentials}>
                    {recognizer && (
                        <Button
                            appearance="transparent"
                            disabled={isListening}
                            icon={<MicRegular />}
                            onClick={handleSpeech}
                        />
                    )}
                    {/* <Button
                        appearance="transparent"
                        icon={<SendRegular />}
                        onClick={() => {
                            handleSubmit(value);
                        }}
                    /> */}
                </div>
            </div>
        </div>
    );
};
